/**
 * JSON-LD Structured Data Helpers
 * Typed builders for schema.org structured data
 */

import type React from "react";
import { getSiteUrl, seoConfig } from "./config";

// Remove undefined fields from objects
function sanitize<T>(obj: T): T {
	return JSON.parse(JSON.stringify(obj)) as T;
}

/**
 * Organization schema
 */
export interface OrganizationSchema {
	name: string;
	url: string;
	logo?: string;
	description?: string;
	sameAs?: string[]; // Social media profiles
}

export function buildOrganization(options: Partial<OrganizationSchema> = {}): object {
	const schema = {
		"@context": "https://schema.org",
		"@type": "Organization",
		name: options.name || seoConfig.siteName,
		url: options.url || getSiteUrl(),
		logo: options.logo,
		description: options.description || seoConfig.siteDescription,
		sameAs: options.sameAs,
	};

	return sanitize(schema);
}

/**
 * WebPage schema
 */
export interface WebPageSchema {
	name: string;
	description: string;
	url: string;
}

export function buildWebPage(options: WebPageSchema): object {
	const schema = {
		"@context": "https://schema.org",
		"@type": "WebPage",
		name: options.name,
		description: options.description,
		url: options.url,
	};

	return sanitize(schema);
}

/**
 * Article schema (for blog posts)
 */
export interface ArticleSchema {
	headline: string;
	description: string;
	author: {
		name: string;
		url?: string;
	};
	datePublished: string; // ISO 8601 format
	dateModified?: string;
	image?: string;
	url: string;
}

export function buildArticle(options: ArticleSchema): object {
	const schema = {
		"@context": "https://schema.org",
		"@type": "Article",
		headline: options.headline,
		description: options.description,
		author: {
			"@type": "Person",
			name: options.author.name,
			url: options.author.url,
		},
		datePublished: options.datePublished,
		dateModified: options.dateModified || options.datePublished,
		image: options.image,
		url: options.url,
		publisher: {
			"@type": "Organization",
			name: seoConfig.siteName,
			url: getSiteUrl(),
		},
	};

	return sanitize(schema);
}

/**
 * FAQPage schema — renders FAQ rich results in Google
 */
export function buildFAQ(items: { question: string; answer: string }[]): object {
	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: items.map((item) => ({
			"@type": "Question",
			name: item.question,
			acceptedAnswer: {
				"@type": "Answer",
				text: item.answer,
			},
		})),
	};
}

/**
 * BreadcrumbList schema — renders breadcrumb trail in Google
 */
export function buildBreadcrumb(items: { name: string; url: string }[]): object {
	return {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: items.map((item, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: item.name,
			item: item.url,
		})),
	};
}

/**
 * WebSite schema — enables site name display in Google search results
 */
export function buildWebSite(): object {
	return sanitize({
		"@context": "https://schema.org",
		"@type": "WebSite",
		name: seoConfig.siteName,
		url: getSiteUrl(),
	});
}

/**
 * SoftwareApplication schema — product rich result with pricing and rating
 */
export function buildSoftwareApplication(options: {
	ratingValue: number;
	ratingCount: number;
	priceCurrency?: string;
	price?: string;
}): object {
	return sanitize({
		"@context": "https://schema.org",
		"@type": "SoftwareApplication",
		name: seoConfig.siteName,
		description: seoConfig.siteDescription,
		url: getSiteUrl(),
		applicationCategory: "BusinessApplication",
		operatingSystem: "Web",
		offers: {
			"@type": "Offer",
			price: options.price ?? "0",
			priceCurrency: options.priceCurrency ?? "USD",
		},
		aggregateRating: {
			"@type": "AggregateRating",
			ratingValue: options.ratingValue,
			ratingCount: options.ratingCount,
			bestRating: 5,
			worstRating: 1,
		},
	});
}

/**
 * Product with multiple Offer schemas — for pricing pages
 */
export function buildProductOffers(
	offers: { name: string; price: number; priceCurrency?: string; description?: string }[]
): object {
	return sanitize({
		"@context": "https://schema.org",
		"@type": "Product",
		name: `${seoConfig.siteName} Plans`,
		description: seoConfig.siteDescription,
		brand: {
			"@type": "Organization",
			name: seoConfig.siteName,
		},
		offers: offers.map((offer) => ({
			"@type": "Offer",
			name: offer.name,
			price: offer.price,
			priceCurrency: offer.priceCurrency ?? "USD",
			description: offer.description,
			url: `${getSiteUrl()}/pricing`,
		})),
	});
}

/**
 * Generate script tag for JSON-LD
 * Returns a script object compatible with TanStack Router's head() scripts array
 */
export function jsonLdScript(schema: object) {
	return {
		type: "application/ld+json",
		children: JSON.stringify(schema),
	} as React.JSX.IntrinsicElements["script"];
}
